"use client";

import type { FormEvent, Ref } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

type NavSearchFieldProps = {
    tone: "light" | "dark";
    query: string;
    placeholder: string;
    panelId: string;
    inputRef: Ref<HTMLInputElement>;
    onQueryChange: (value: string) => void;
    onClearOrClose: () => void;
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    className?: string;
};

const focusRing = {
    light: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-button-primary focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    dark: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-nav-bg",
} as const;

const NavSearchField = ({
    tone,
    query,
    placeholder,
    panelId,
    inputRef,
    onQueryChange,
    onClearOrClose,
    onSubmit,
    className,
}: NavSearchFieldProps) => {
    const hasQuery = query.trim().length > 0;

    return (
        <form role="search" onSubmit={onSubmit} className={cn("min-w-0 flex-1", className)}>
            <div className="flex min-w-0 items-center gap-3">
                <Search
                    className={cn(
                        "size-6 shrink-0",
                        tone === "light" ? "text-cta-primary" : "text-white",
                    )}
                    strokeWidth={1.5}
                    aria-hidden
                />
                <label className="flex min-w-0 flex-1 flex-col gap-2">
                    <span className="sr-only">{placeholder}</span>
                    <input
                        ref={inputRef}
                        type="search"
                        value={query}
                        onChange={(event) => onQueryChange(event.target.value)}
                        placeholder={placeholder}
                        aria-controls={panelId}
                        aria-autocomplete="list"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        enterKeyHint="search"
                        className={cn(
                            "w-full bg-transparent text-base font-regular leading-[22px] outline-none placeholder:text-field-neutral-400 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
                            tone === "light" ? "text-ink-header" : "text-white",
                            "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                            tone === "light"
                                ? "focus-visible:ring-button-primary focus-visible:ring-offset-white"
                                : "focus-visible:ring-white focus-visible:ring-offset-nav-bg",
                        )}
                    />
                    <span
                        className={cn(
                            "h-px w-full",
                            tone === "light" ? "bg-badge-neutral-300" : "bg-white/40",
                        )}
                    />
                </label>
                <button
                    type="button"
                    className={cn(
                        "inline-flex size-6 shrink-0 items-center justify-center rounded-sm",
                        tone === "light" ? "text-ink-header" : "text-white",
                        focusRing[tone],
                    )}
                    aria-label={hasQuery ? "Clear search" : "Close search"}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={onClearOrClose}
                >
                    <X className="size-6" strokeWidth={1.5} aria-hidden />
                </button>
            </div>
        </form>
    );
};

export default NavSearchField;
